import React, { useEffect, useState, useCallback } from "react";
import {
  CheckCircle2, LogOut, ArrowRightLeft, HeartCrack, Lock, AlertTriangle, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";

const DISPO_ROLES = ["admin", "hah_physician", "physician", "hah_coordinator", "care_coordinator", "hah_nurse", "nurse"];
const ICONS = { check: CheckCircle2, logout: LogOut, transfer: ArrowRightLeft, cross: HeartCrack };

// Field spec per disposition type. opt = key into meta.options[type].
const SPECS = {
  discharged: [
    { k: "condition_at_discharge", label: "Condition at discharge", type: "select", opt: "condition_at_discharge" },
    { k: "destination", label: "Discharge destination", type: "select", opt: "destination" },
    { k: "functional_status", label: "Functional status", type: "select", opt: "functional_status" },
    { k: "follow_up", label: "Follow-up plan", type: "select", opt: "follow_up" },
    { k: "follow_up_date", label: "Follow-up date", type: "date", showIf: (r) => r.follow_up && r.follow_up !== "None required" },
    { k: "meds_reconciled", label: "Medications reconciled", type: "ack" },
    { k: "caregiver_educated", label: "Patient / caregiver educated", type: "ack" },
    { k: "override_reason", label: "Override reason (only if discharge criteria not all met)", type: "text", optional: true },
  ],
  left_program: [
    { k: "reason", label: "Reason for leaving", type: "select", opt: "reason" },
    { k: "condition_at_exit", label: "Condition at exit", type: "select", opt: "condition_at_exit" },
    { k: "follow_up_advice", label: "Follow-up advice given", type: "select", opt: "follow_up_advice", optional: true },
    { k: "risks_explained", label: "Risks of leaving explained to patient", type: "ack" },
    { k: "acknowledged", label: "Patient acknowledged leaving the program", type: "ack" },
    { k: "ama_form_signed", label: "AMA acknowledgement signed", type: "ack", showIf: (r) => r.reason === "Against medical advice (AMA)" },
  ],
  transferred: [
    { k: "transfer_type", label: "Transfer type", type: "select", opt: "transfer_type" },
    { k: "receiving_facility", label: "Receiving facility", type: "text" },
    { k: "reason", label: "Reason for transfer", type: "select", opt: "reason" },
    { k: "transport_mode", label: "Transport mode", type: "select", opt: "transport_mode" },
    { k: "condition_at_transfer", label: "Condition at transfer", type: "select", opt: "condition_at_transfer", optional: true },
    { k: "documents_sent", label: "Transfer-of-care documents sent", type: "ack" },
  ],
  deceased: [
    { k: "date_time_of_death", label: "Date & time of death", type: "datetime" },
    { k: "place_of_death", label: "Place of death", type: "select", opt: "place_of_death" },
    { k: "expectedness", label: "Expectedness", type: "select", opt: "expectedness" },
    { k: "death_certificate", label: "Death certificate", type: "select", opt: "death_certificate" },
    { k: "clinician_verified", label: "Death verified by attending clinician", type: "ack" },
    { k: "family_notified", label: "Family notified", type: "ack", optional: true },
    { k: "coroner_referral", label: "Coroner / medico-legal referral needed", type: "ack", optional: true },
  ],
};
const ALL_FIELDS = { disposition_date: "Disposition date", notes: "Notes", ...Object.fromEntries(Object.values(SPECS).flat().map((f) => [f.k, f.label])) };

function ActionCard({ type, onClick, disabled }) {
  const Icon = ICONS[type.icon] || CheckCircle2;
  return (
    <button onClick={onClick} disabled={disabled} data-testid={`dispo-action-${type.key}`}
      className="hah-card p-5 text-left hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed group">
      <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-3" style={{ backgroundColor: `${type.color}1A`, color: type.color }}>
        <Icon className="w-6 h-6" />
      </div>
      <h4 className="text-sm font-semibold text-[#1F2937] group-hover:text-[#0B7E8C]">{type.label}</h4>
      <p className="text-xs text-[#6B7280] mt-1">{type.description}</p>
    </button>
  );
}

export default function DispositionTab({ patientId, onChange }) {
  const { user } = useAuth();
  const canDispose = DISPO_ROLES.includes(user?.role_code);
  const [meta, setMeta] = useState(null);
  const [record, setRecord] = useState(undefined); // undefined=loading, null=none
  const [active, setActive] = useState(null);       // type being filled
  const [report, setReport] = useState({});
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [m, r] = await Promise.all([api.get("/dispositions/meta"), api.get(`/dispositions/patient/${patientId}`)]);
      setMeta(m.data); setRecord(r.data.disposition);
    } catch (e) { toast.error(formatApiError(e)); setRecord(null); }
  }, [patientId]);
  useEffect(() => { load(); }, [load]);

  const openAction = (type) => {
    setActive(type);
    setReport({ disposition_date: new Date().toISOString().slice(0, 10) });
    setConfirmed(false);
  };
  const upd = (k, v) => setReport((r) => ({ ...r, [k]: v }));

  const submit = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/dispositions/patient/${patientId}`, { type: active.key, report });
      toast.success(`Patient dispositioned — ${active.label}`);
      setActive(null);
      setRecord(res.data.disposition);
      onChange && onChange(res.data.patient);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  if (record === undefined || !meta) return <div className="py-8 text-sm text-[#6B7280]" data-testid="dispo-loading">Loading…</div>;

  // ── Already dispositioned → locked read-only view ──
  if (record) {
    const t = (meta.types || []).find((x) => x.key === record.type) || {};
    const Icon = ICONS[t.icon] || CheckCircle2;
    const entries = Object.entries(record.report || {}).filter(([, v]) => v !== "" && v !== null && v !== undefined);
    return (
      <div className="space-y-4" data-testid="disposition-locked">
        <div className="hah-card p-5 border-l-4" style={{ borderLeftColor: t.color }}>
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${t.color}1A`, color: t.color }}><Icon className="w-6 h-6" /></div>
              <div>
                <h3 className="text-base font-semibold text-[#1F2937]">{record.label}</h3>
                <div className="text-xs text-[#6B7280] mt-0.5">By {record.dispositioned_by_name} · {String(record.dispositioned_at).slice(0, 16).replace("T", " ")}{record.episode_ref ? ` · ${record.episode_ref}` : ""}</div>
              </div>
            </div>
            <span className="inline-flex items-center gap-1 text-xs text-[#6B7280] px-2 py-1 rounded-full bg-[#F1F5F9]"><Lock className="w-3 h-3" />Locked</span>
          </div>
          {(record.warnings || []).length > 0 && (
            <div className="mt-3 p-2 rounded-lg bg-[#FFFBEB] border border-[#FDE68A] text-xs text-[#92400E]">
              {record.warnings.map((w, i) => <div key={i} className="flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" />{w}</div>)}
            </div>
          )}
        </div>
        <div className="hah-card p-5" data-testid="dispo-report">
          <h4 className="text-sm font-semibold text-[#1F2937] mb-3">Disposition Report</h4>
          <dl className="grid md:grid-cols-2 gap-x-6 gap-y-2">
            {entries.map(([k, v]) => (
              <div key={k} className="flex justify-between gap-3 py-1 border-b border-[#F1F5F9]">
                <dt className="text-xs text-[#6B7280]">{ALL_FIELDS[k] || k}</dt>
                <dd className="text-sm text-[#1F2937] text-right">{typeof v === "boolean" ? (v ? "Yes" : "No") : String(v)}</dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
    );
  }

  // ── Not yet dispositioned → action cards ──
  return (
    <div data-testid="disposition-tab">
      {!canDispose && <div className="p-3 mb-3 rounded-lg bg-[#EFF6FF] border border-[#BFDBFE] text-sm text-[#1E40AF]">Your role can view but not disposition patients.</div>}
      <p className="text-sm text-[#6B7280] mb-3">Choose how this patient exits the program. Each action opens a structured report and is <b>permanent</b> once saved.</p>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
        {(meta.types || []).map((t) => <ActionCard key={t.key} type={t} disabled={!canDispose} onClick={() => openAction(t)} />)}
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="dispo-dialog">
          <DialogHeader><DialogTitle>{active?.label}</DialogTitle></DialogHeader>
          {active && (
            <div className="space-y-3">
              <div>
                <label className="block text-[11px] uppercase tracking-wider font-semibold text-[#6B7280] mb-1">Disposition date *</label>
                <Input type="date" value={report.disposition_date || ""} onChange={(e) => upd("disposition_date", e.target.value)} data-testid="dispo-date" />
              </div>
              {SPECS[active.key].filter((f) => !f.showIf || f.showIf(report)).map((f) => (
                <div key={f.k}>
                  {f.type !== "ack" && <label className="block text-[11px] uppercase tracking-wider font-semibold text-[#6B7280] mb-1">{f.label}{!f.optional ? " *" : ""}</label>}
                  {f.type === "select" && (
                    <Select value={report[f.k] || ""} onValueChange={(v) => upd(f.k, v)}>
                      <SelectTrigger data-testid={`dispo-f-${f.k}`}><SelectValue placeholder="Select…" /></SelectTrigger>
                      <SelectContent>{(meta.options[active.key][f.opt] || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                    </Select>
                  )}
                  {f.type === "text" && <Input value={report[f.k] || ""} onChange={(e) => upd(f.k, e.target.value)} data-testid={`dispo-f-${f.k}`} />}
                  {f.type === "date" && <Input type="date" value={report[f.k] || ""} onChange={(e) => upd(f.k, e.target.value)} data-testid={`dispo-f-${f.k}`} />}
                  {f.type === "datetime" && <Input type="datetime-local" value={report[f.k] || ""} onChange={(e) => upd(f.k, e.target.value)} data-testid={`dispo-f-${f.k}`} />}
                  {f.type === "ack" && (
                    <label className="flex items-center gap-2 p-2 rounded-lg border border-[#E5E7EB] cursor-pointer">
                      <Checkbox checked={!!report[f.k]} onCheckedChange={(c) => upd(f.k, !!c)} data-testid={`dispo-f-${f.k}`} />
                      <span className="text-sm text-[#1F2937]">{f.label}{!f.optional ? " *" : ""}</span>
                    </label>
                  )}
                </div>
              ))}
              <div>
                <label className="block text-[11px] uppercase tracking-wider font-semibold text-[#6B7280] mb-1">Notes</label>
                <Textarea rows={2} value={report.notes || ""} onChange={(e) => upd("notes", e.target.value)} placeholder="Optional short note…" data-testid="dispo-notes" />
              </div>
              <label className="flex items-center gap-2 p-2.5 rounded-lg bg-[#FEF2F2] border border-[#FECACA] cursor-pointer">
                <Checkbox checked={confirmed} onCheckedChange={(c) => setConfirmed(!!c)} data-testid="dispo-confirm" />
                <span className="text-sm text-[#991B1B] flex items-center gap-1.5"><ShieldCheck className="w-4 h-4" />I confirm this disposition is final and cannot be changed afterwards.</span>
              </label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setActive(null)} data-testid="dispo-cancel">Cancel</Button>
            <Button onClick={submit} disabled={busy || !confirmed || !report.disposition_date} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="dispo-submit">{busy ? "Saving…" : "Confirm disposition"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
